// Ui.jsx — ZDIEĽANÉ UI KOMPONENTY pre celý Matrix v2 (ústava sek. 56.8)
// Jediný zdroj pravdy pre KPI karty, filter chips, taby, master-detail layout.
// Zmena tu = zmena vo VŠETKÝCH moduloch naraz (Partneri, Zákazky, Faktúry, Mzdy...).
//
// Štandard odvodený z PartneriView (ústava sek. 21.15 farby, 53.8 DIFF, 56 master-detail, 57 jazyk).

import React, { useState, useMemo, useRef, useEffect } from 'react'
import { isAnyDirty, confirmLeave } from '../hooks/unsavedGuard.js'   // straz_v1 (28. 9. 2026): prepnutie záznamu a záložky sa pýta na neuložené zmeny (ústava 53.1)
import { createPortal } from 'react-dom'
import { COLORS, TYPO, SPACE, RADIUS, SK, norm } from '../theme.js'
import { fetchAuth } from '../auth/fetchAuth.js'   // nervy_fe_v1: pásik nervovej sústavy číta /api/nervy (ústava 138.3)

// ══════════════════════════════════════════════════════════════
// KPI KARTA — borderLeft 3px, RADIUS.lg, titulok UPPERCASE
// Použitie: <KpiCard title="..." color={COLORS.nav}>{obsah}</KpiCard>
//   alebo skratky: <KpiCard title count sum /> · <KpiCard title value />
// ══════════════════════════════════════════════════════════════
// KPI karta — KOMPAKTNÝ pásik (ústava 92.6, Mirko 2026-06-09 „widgety berú miesto").
// Sémantická farba stavu = ĽAVÝ okraj (nie hrubý vrchný pás), label + číslo na malej výške.
export function KpiCard({ title, color = COLORS.nav, onClick, highlight, count, sum, value, children, fmtEur }) {
  return (
    <div onClick={onClick}
      style={{
        background: highlight ? COLORS.bgSoft : COLORS.bg,
        border: `1px solid ${COLORS.line}`,
        borderLeft: `4px solid ${color}`,
        borderRadius: RADIUS.md,
        padding: '5px 10px',
        cursor: onClick ? 'pointer' : 'default',
        display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2,
        minWidth: 0
      }}
      title={onClick ? 'Klik = filter' : ''}
    >
      <div style={{
        fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: COLORS.muted,
        textTransform: 'uppercase', letterSpacing: '0.02em',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
      }}>{title}</div>
      {children ? children : value !== undefined ? (
        <div style={{ fontSize: TYPO.sizes.lg, fontWeight: TYPO.weights.extra, color: COLORS.text, fontFamily: TYPO.mono, lineHeight: 1 }}>
          {value}
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
          <span style={{ fontSize: TYPO.sizes.lg, fontWeight: TYPO.weights.extra, color: COLORS.text, lineHeight: 1 }}>{count}</span>
          {sum !== undefined && (
            <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontFamily: TYPO.mono, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Σ {fmtEur ? fmtEur(sum) : SK.eur(sum)}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

// Rad KPI kariet — grid
export function KpiRow({ cols = 3, children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: SPACE.md }}>
      {children}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════
// FILTER CHIPS — RADIUS.sm, flex:1, transparent inactive
// Použitie: <FilterChips options={[{key,label,color}]} value={x} onChange={fn} />
// ══════════════════════════════════════════════════════════════
export function FilterChips({ options, value, onChange, equalWidth = true }) {
  // vzhlad_filtre_v1 (8. 10. 2026, Mirko: „esteticky — nevycentrované"): pilulky mali
  // `flex: 1`, takže si delili riadok rovným dielom a posledná ostávala sama cez celú
  // šírku. Podľa ústavy 61.9 je filter slovo s linkou — slová sa poskladajú podľa
  // svojej dĺžky a nič sa neťahá. Farbu dostane len ten, ktorý je zapnutý.
  // vzhlad_predajne_v1 (61.10): filter je kartička s rámčekom ako v Predajni; zapnutý je plný a biely text.
  return (
    <div style={{ padding: `${SPACE.sm}px ${SPACE.md}px`, display: 'flex', flexWrap: 'wrap',
      gap: 6, borderBottom: `1px solid ${COLORS.line}` }}>
      {options.map(o => {
        const active = value === o.key
        const c = o.color || COLORS.nav
        return (
          <button key={o.key} onClick={() => onChange(o.key)} title={o.hint || ''}
            style={{
              padding: '4px 12px',
              border: `1px solid ${active ? c : COLORS.line}`,
              borderRadius: 16,
              background: active ? c : COLORS.bg,
              fontSize: TYPO.sizes.sm,
              fontWeight: active ? TYPO.weights.semibold : TYPO.weights.normal,
              color: active ? '#fff' : COLORS.text,
              cursor: 'pointer',
              fontFamily: 'inherit',
              whiteSpace: 'nowrap'
            }}
          >{o.label}</button>
        )
      })}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════
// TABS — borderBottom 2px underline (ústava sek. 56, PartnerDetail štandard)
// Použitie: <Tabs tabs={[{key,label,active}]} value={x} onChange={fn} />
// ══════════════════════════════════════════════════════════════
export function Tabs({ tabs, value, onChange }) {
  const zmen = async (k) => { if (k === value) return; if (isAnyDirty() && !(await confirmLeave())) return; onChange(k) }   // straz_v1
  return (
    <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${COLORS.line}`, marginBottom: SPACE.md, flexWrap: 'wrap' }}>
      {tabs.map(t => {
        const isActive = value === t.key
        const enabled = t.active !== false
        return (
          <button key={t.key}
            disabled={!enabled}
            onClick={() => enabled && zmen(t.key)}
            style={{
              padding: '8px 16px',
              fontSize: TYPO.sizes.sm,
              fontWeight: TYPO.weights.semibold,
              color: isActive ? COLORS.nav : (enabled ? COLORS.muted : COLORS.disabled),
              background: 'transparent',
              border: 'none',
              borderBottom: `2px solid ${isActive ? COLORS.nav : 'transparent'}`,
              cursor: enabled ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit',
              marginBottom: -1,
              opacity: enabled ? 1 : 0.45
            }}
            title={enabled ? '' : 'Pripravujem'}
          >{t.label}{t.count !== undefined && (
            <span style={{ opacity: 0.7, fontWeight: TYPO.weights.normal }}> ({t.count})</span>
          )}</button>
        )
      })}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════
// EMPTY PANE — placeholder pre prázdny detail
// ══════════════════════════════════════════════════════════════
export function EmptyPane({ text = 'Vyber položku zo zoznamu', icon = '' }) {
  return (
    <div style={{
      background: COLORS.bg,
      border: `1px solid ${COLORS.line}`,
      borderRadius: RADIUS.lg,
      padding: SPACE.xxl,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: COLORS.muted,
      fontSize: TYPO.sizes.base
    }}>
      <div style={{ fontSize: 48, marginBottom: SPACE.md }}>{icon}</div>
      <div>{text}</div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════
// MASTER-DETAIL LAYOUT — ľavý zoznam 420px + pravý detail (ústava sek. 56)
// Použitie:
//   <MasterDetail
//     left={<...zoznam...>}
//     right={selected ? <Detail/> : <EmptyPane/>}
//   />
// ══════════════════════════════════════════════════════════════
export function MasterDetail({ left, right, leftWidth = 300, filterBar }) {
  // 2026-07-24 (Mirko „a ľavý stĺpec?!"): zbalenie ľavého zoznamu PER MODUL. Predtým jeden globálny kľúč
  // 'md_list_hidden' => zbalenie v jednom module skrylo ľavý stĺpec VO VŠETKÝCH (porušenie ústavy 56.1).
  const _mdKey = 'md_hidden:' + ((typeof window !== 'undefined' && window.location ? window.location.pathname : '').split('/').slice(0, 3).join('/') || '_')
  const [hidden, setHidden] = useState(() => { try { return localStorage.getItem(_mdKey) === '1' } catch(_) { return false } })
  const toggle = () => setHidden(h => { const n = !h; try { localStorage.setItem(_mdKey, n ? '1' : '0') } catch(_) {}; return n })
  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* ústava 90 — horizontálny filter pás, ľavý stĺpec ostáva čistý */}
      {filterBar && <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: SPACE.sm, padding: '8px 10px', marginBottom: SPACE.sm, background: COLORS.bgSoft, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md }}>{filterBar}</div>}
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
      {/* stlpec_jednotny_v1 (Mirko 13. 10. 2026) — DVE jeho vyty z jedneho vecera maju jednu pricinu:
          „tento typ stlpca je pekny uhladany, prehladny – napr. zamestanci strasne, CP napr chyba im lem
          musi to byt jednotne" (so snimkou stlpca ZAKAZIEK) a „pouzivame oranzovu ako logo kamir podobne
          ako je tlacidlo napady, preco ju vynechavas?"
          ZISTENE: ZakazkyView si kresli stlpec po svojom a ako JEDINY si podrzal oranzovy panel
          rgba(239,147,80,0.10) z povodnej varianty D (ustava 61.7) — davky vzhlad_v1–v4 zo 7. 10. ho
          vsade inde zrusili. Mirkovi sa teda paci prave to, co sa inde zmazalo.
          Oranzova sa vracia ako ZNACKA na panel (61.9 zakazuje oranzovu plochu POD OBSAHOM, nie znacku);
          pasik na riadku dalej dostane len to, co si ziada pozornost. */}
      {!hidden && <div style={{
        width: leftWidth, flexShrink: 0,
        background: COLORS.bg,   // vzhlad_predajne_v1 (61.10): zoznam je biela karta na sivom pozadí
        border: `1px solid ${COLORS.line}`,
        borderRadius: RADIUS.lg,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>{left}</div>}
      <div onClick={toggle} title={hidden ? 'Zobraziť zoznam zákaziek' : 'Skryť zoznam — plná šírka pre detail'}
        style={{
          width: 20, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', color: COLORS.brandD, fontWeight: 800, fontSize: 14, userSelect: 'none',
        }}>{hidden ? '»' : '«'}</div>
      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>{right}</div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════
// PAGE HEADER — h1 + počet + akčné tlačidlo (ústava sek. 57)
// ══════════════════════════════════════════════════════════════
export function PageHeader({ title, subtitle, children }) {
  // vzhlad_v1 (7. 10. 2026, ústava 61.9): značka KAMIR ostáva ako pásik, oranžové pozadie panela je preč.
  // Nadpis drží linka, nie farebná plocha.
  return (
    <div style={{ marginBottom: SPACE.sm, background: COLORS.bg,   // vzhlad_predajne_v1 (61.10): hlavička je biela karta · plocha_v1: jeden tenký riadok
      border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg, overflow: 'hidden' }}>
      {/* oranžový pásik + jemné oranžové pozadie hlavičky modulu (ústava 61.6) — KOMPAKTNÉ (Mirko 2026-06-09) */}
      <div style={{ height: 3, background: COLORS.brand }} />
      <div style={{ display: 'flex', alignItems: 'baseline', gap: SPACE.md, flexWrap: 'wrap',
        padding: `4px ${SPACE.md}px`, minHeight: 36 }}>
        <h1 style={{ margin: 0, fontSize: TYPO.sizes.lg, fontWeight: TYPO.weights.extra, color: COLORS.base, whiteSpace: 'nowrap' }}>{title}</h1>
        {subtitle && <span style={{ color: COLORS.muted, fontSize: TYPO.sizes.base }}>{subtitle}</span>}
        <div style={{ flex: 1 }} />
        {children}
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════
// NERVY — jedno počítadlo z nervovej sústavy (ústava 138.3)
// Jedna veta, a je to TÁ ISTÁ veta, akou to povie asistent Anton.
// Dá sa na ňu kliknúť: klik = filter zoznamu na to, o čom veta hovorí.
// Anton len upozorní — stlačiť tlačidlo je vždy na človeku (ústava 138.2).
// Použitie: <NervyPas tab="ponuky" vybrane={filter} onVyber={setFilter} />
// ══════════════════════════════════════════════════════════════
export function NervyPas({ tab, onVyber, vybrane }) {
  const [hori, setHori] = useState([])
  useEffect(() => {
    let zive = true
    try {
      fetchAuth('/api/nervy/' + tab)
        .then(r => (r && r.ok ? r.json() : null))
        .then(d => { if (zive && d && Array.isArray(d.hori)) setHori(d.hori) })
        .catch(() => {})
    } catch (_) {}
    return () => { zive = false }
  }, [tab])
  if (!hori.length) return null
  // Farbu dostane len to, čo horí (61.9). Poriadok v dátach je tichý.
  const farba = (n) => (n >= 3 ? COLORS.remove : (n === 2 ? COLORS.total : COLORS.muted))
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACE.md, alignItems: 'baseline',
      padding: `6px ${SPACE.md}px`, borderBottom: `1px solid ${COLORS.line}` }}>
      {hori.map(h => {
        const je = vybrane === h.kluc
        return (
          <button key={h.kluc} type="button"
            onClick={() => onVyber && onVyber(je ? 'all' : h.kluc, h)}
            title={h.akcia ? (h.akcia.nazov + ' — stláča ' + h.akcia.stlaci) : ''}
            style={{ border: 0, background: 'none', padding: '2px 0', font: 'inherit',
              cursor: onVyber ? 'pointer' : 'default', fontSize: TYPO.sizes.sm,
              color: je ? COLORS.base : farba(h.naliehavost),
              fontWeight: je ? TYPO.weights.semibold : TYPO.weights.normal,
              borderBottom: `2px solid ${je ? COLORS.base : 'transparent'}` }}>
            {h.veta}
          </button>
        )
      })}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════
// LIST ROW — riadok master zoznamu (ústava sek. 56)
// Použitie: <ListRow selected={bool} onClick onDoubleClick>{obsah}</ListRow>
// ══════════════════════════════════════════════════════════════
export function ListRow({ selected, onClick, onDoubleClick, dataId, dataAttr = 'data-row-id', children, title, stripColor }) {
  const attrs = { [dataAttr]: dataId }
  // variant D — biela karta na oranzovom paneli + lavy stavovy pasik (ustava 61.7)
  // vzhlad_v1 (7. 10. 2026): keď má pásik každý riadok, nerozlíši nič — v Reklamáciách hlásilo
  // „po termíne" 22 z 22. Pásik dostane len riadok, ktorý ho naozaj má (stripColor), alebo vybraný riadok.
  const strip = stripColor || (selected ? COLORS.nav : 'transparent')
  // straz_v1: klik na iný záznam pri rozpísanej zmene → Uložiť / Zahodiť / Zrušiť (ústava 53.1); rovnaký riadok sa nepýta
  const straz = (fn) => fn ? (async (e) => { if (!selected && isAnyDirty() && !(await confirmLeave())) return; fn(e) }) : undefined
  return (
    <div {...attrs}
      onClick={straz(onClick)}
      onDoubleClick={straz(onDoubleClick)}
      title={title}
      style={{
        // stlpec_jednotny_v1: riadok je BIELA KARTA s lemom a medzerou — presne ako v Zakazkach,
        // ktore Mirko oznacil za vzor. Doteraz to bol plochy zoznam oddeleny vlasovou linkou:
        // na papierovom paneli splyval a Mirko napisal „zlieva sa to do jedneho celku".
        // Vyber drzi prsten + pasik, nie zmena pozadia — karta je biela vzdy.
        padding: '10px 12px',
        marginBottom: 6,
        background: selected ? COLORS.navBg : COLORS.bg,   // vzhlad_predajne_v1 (61.10): vybraný ako v Predajni
        border: `1px solid ${selected ? COLORS.nav : COLORS.line}`,
        borderLeft: `3px solid ${strip}`,
        borderRadius: RADIUS.md,
        boxShadow: selected ? 'none' : '0 1px 2px rgba(0,0,0,0.05)',
        cursor: 'pointer',
        fontSize: TYPO.sizes.sm,
        userSelect: 'none'
      }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.12)' }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)' }}
    >{children}</div>
  )
}

// ══════════════════════════════════════════════════════════════
// SEARCH INPUT — type=search, fontSize sm (ústava sek. 56)
// ══════════════════════════════════════════════════════════════
export function SearchInput({ value, onChange, placeholder = 'Hľadať (stlač /)…', dataAttr }) {
  const attrs = dataAttr ? { [dataAttr]: true } : {}
  return (
    <div style={{ padding: SPACE.sm, borderBottom: `1px solid ${COLORS.line}` }}>
      <input {...attrs}
        type="search"
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: '6px 10px',
          fontSize: TYPO.sizes.sm,
          fontFamily: 'inherit',
          border: `1px solid ${COLORS.line}`,
          borderRadius: RADIUS.md,
          outline: 'none',
          boxSizing: 'border-box'
        }}
      />
    </div>
  )
}

// ══════════════════════════════════════════════════════════════
// STATUS BAR — spodný pruh zoznamu (klávesové skratky)
// ══════════════════════════════════════════════════════════════
export function ListStatusBar({ children }) {
  return (
    <div style={{
      padding: '6px 12px',
      borderTop: `1px solid ${COLORS.line}`,
      fontSize: TYPO.sizes.xs,
      color: COLORS.muted,
      background: COLORS.bgSoft
    }}>{children}</div>
  )
}


// ══════════════════════════════════════════════════════════════
// SEARCHABLE SELECT — searchable dropdown (ústava sek. 56.11)
// Povinné pre KAŽDÝ dropdown > 10 položiek (zákazky, zamestnanci, PLU…)
// Použitie:
//   <SearchableSelect value={x} onChange={fn}
//      options={[{value,label}]} placeholder="Hľadať…"
//      emptyLabel="— bez zákazky —" />   // emptyLabel = voliteľná prázdna voľba
// ══════════════════════════════════════════════════════════════
// p107 (chrbtica kontaktov, 22. 9. 2026) — doplnky, pôvodné správanie sa nemení: option.hladat = skrytý text, podľa ktorého
// sa tiež hľadá (číslo, e-mail, firma); option.popis = druhý riadok pod názvom; createLabel(t) = text voľby „použiť napísané".
export function SearchableSelect({ value, onChange, options = [], placeholder = 'Hľadať…', emptyLabel = null, error = false, allowCreate = false, menuPortal = false, createLabel = null }) {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const blurTimer = useRef(null)
  const inputRef = useRef(null)
  const [menuRect, setMenuRect] = useState(null)
  useEffect(() => {
    if (!open || !menuPortal) return
    const upd = () => { if (inputRef.current) setMenuRect(inputRef.current.getBoundingClientRect()) }
    upd()
    window.addEventListener('resize', upd)
    window.addEventListener('scroll', upd, true)
    return () => { window.removeEventListener('resize', upd); window.removeEventListener('scroll', upd, true) }
  }, [open, menuPortal])

  const selected = options.find(o => o.value === value)
  const selectedLabel = selected ? selected.label : (value ? String(value) : (emptyLabel || ''))

  const filtered = useMemo(() => {
    const base = emptyLabel ? [{ value: '', label: emptyLabel }, ...options] : options
    if (!search.trim()) return base
    const q = norm(search)   // V3: bez diakritiky (66.1) — „kovac“ nájde „Kováč“
    const matched = base.filter(o => norm((o.label || '') + ' ' + (o.hladat || '')).includes(q))
    if (allowCreate) {
      const t = search.trim()
      const exists = base.some(o => (o.label || '').toLowerCase() === t.toLowerCase() || (o.value || '').toString().toLowerCase() === t.toLowerCase())
      if (t && !exists) matched.push({ value: t, label: createLabel ? createLabel(t) : `+ Pridať: „${t}"`, __create: true })
    }
    return matched
  }, [options, search, emptyLabel, allowCreate])

  function pick(o) {
    onChange(o.value)
    setSearch('')
    setOpen(false)
  }

  function onKey(e) {
    if (!open) { if (e.key === 'ArrowDown' || e.key === 'Enter') { setOpen(true) } ; return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(filtered.length - 1, h + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(0, h - 1)) }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[highlight]) pick(filtered[highlight]) }
    else if (e.key === 'Escape') { setOpen(false); setSearch('') }
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        value={open ? search : selectedLabel}
        placeholder={placeholder}
        onChange={e => { setSearch(e.target.value); setOpen(true); setHighlight(0) }}
        onFocus={() => { setSearch(''); setOpen(true); setHighlight(0) }}
        onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 200) }}
        onKeyDown={onKey}
        style={{
          width: '100%',
          padding: '6px 10px',
          fontSize: TYPO.sizes.base,
          fontFamily: 'inherit',
          border: `1px solid ${error ? COLORS.remove : COLORS.line}`,
          borderRadius: RADIUS.md,
          background: COLORS.bg,
          outline: 'none',
          boxSizing: 'border-box'
        }}
      />
      {open && (() => {
        const menuBase = {
          background: COLORS.bg, border: `1px solid ${COLORS.line}`,
          borderRadius: RADIUS.md, boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        }
        const usePortal = menuPortal && menuRect
        let portalStyle = null
        if (usePortal) {
          const spaceBelow = window.innerHeight - menuRect.bottom - 8
          const spaceAbove = menuRect.top - 8
          const openUp = spaceBelow < 180 && spaceAbove > spaceBelow   // flip nahor ak dole nie je miesto
          const avail = Math.max(120, openUp ? spaceAbove : spaceBelow)
          portalStyle = {
            ...menuBase, position: 'fixed', left: menuRect.left, width: menuRect.width,
            zIndex: 9500, // 5.8.2026: modaly maju 8000 — menu s 4000 bolo ZA modalom (nevidne, neklikatelne; "nefunguje tlacidlo")
            maxHeight: Math.min(260, avail), overflow: 'auto',
            ...(openUp ? { bottom: window.innerHeight - menuRect.top + 2 } : { top: menuRect.bottom + 2 }),
          }
        }
        const inner = (
          <div onMouseDown={() => clearTimeout(blurTimer.current)}
            style={usePortal
              ? portalStyle
              : { position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 2, zIndex: 200, maxHeight: 260, overflow: 'auto', ...menuBase }}
          >
            {filtered.length === 0 ? (
              <div style={{ padding: SPACE.md, color: COLORS.muted, fontSize: TYPO.sizes.sm, textAlign: 'center' }}>
                Žiadna zhoda
              </div>
            ) : filtered.map((o, i) => (
              <div key={o.value || '__empty'}
                onMouseDown={e => { e.preventDefault(); pick(o) }}
                onMouseEnter={() => setHighlight(i)}
                style={{
                  padding: '6px 12px', cursor: 'pointer',
                  fontSize: TYPO.sizes.sm,
                  background: i === highlight ? (o.__create ? 'rgba(245,158,11,0.12)' : COLORS.navBg) : 'transparent',
                  color: o.__create ? '#f59e0b' : (o.value === '' ? COLORS.muted : COLORS.text),
                  fontWeight: o.__create ? TYPO.weights.semibold : 'inherit',
                  borderBottom: `1px solid ${COLORS.lineSoft}`,
                  whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3
                }}
              >{o.label}{o.popis && <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 1 }}>{o.popis}</div>}</div>
            ))}
          </div>
        )
        return usePortal ? createPortal(inner, document.body) : inner
      })()}
    </div>
  )
}

export default { KpiCard, KpiRow, FilterChips, Tabs, EmptyPane, MasterDetail, PageHeader, ListRow, SearchInput, ListStatusBar, SearchableSelect }

// ══════════════════════════════════════════════════════════════
// SEKCIA — jedna časť obsahu karty ako samostatná biela karta (ústava 61.10, sekcie_v1 20. 10. 2026)
// Mirko 19. 10.: „treba oddelit nejako splyva to do jedneho nedá sa rychlo orientovat. Toto je prehladné" (Predajňa).
// Použitie: <Sekcia nazov="Náklady zákazky" pocet={6} popis="bez DPH" akcie={<Btn/>} napoveda="…">…</Sekcia>
//   ploche = bez nadpisu (len rám) · nadpis veľkými písmenami sivou ako v Predajni · žiadne guličky (61.8)
// ══════════════════════════════════════════════════════════════
export function Sekcia({ nazov, pocet, popis, akcie, napoveda, ploche = false, children, style = {} }) {
  const [nap, setNap] = useState(false)
  return (
    <section style={{
      background: COLORS.bg, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg,
      padding: ploche ? `${SPACE.md}px 14px` : `2px 14px ${SPACE.md}px`, marginBottom: SPACE.md, minWidth: 0, ...style,
    }}>
      {!ploche && (nazov || akcie) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm, padding: `${SPACE.md}px 0 ${SPACE.sm}px`, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: TYPO.sizes.base, fontWeight: TYPO.weights.bold, color: COLORS.muted,
            textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: SPACE.sm }}>
            {nazov}
            {pocet !== undefined && pocet !== null && <span style={{ fontWeight: TYPO.weights.normal, textTransform: 'none', letterSpacing: 0, fontSize: TYPO.sizes.sm }}>{pocet}</span>}
          </h3>
          {popis && <span style={{ fontSize: TYPO.sizes.sm, color: COLORS.muted }}>{popis}</span>}
          {napoveda && (
            <span style={{ position: 'relative' }}>
              <button type="button" aria-label="Vysvetlenie" onClick={() => setNap(n => !n)} onBlur={() => setNap(false)}
                style={{ width: 20, height: 20, borderRadius: RADIUS.full, border: `1px solid ${COLORS.line}`, background: COLORS.bg,
                  color: COLORS.muted, cursor: 'pointer', fontSize: TYPO.sizes.sm, padding: 0, fontFamily: 'inherit' }}>?</button>
              {nap && <span style={{ position: 'absolute', left: 0, top: 24, zIndex: 20, width: 'min(460px, 70vw)', background: COLORS.base,
                color: '#fff', padding: '10px 12px', fontSize: TYPO.sizes.sm, lineHeight: 1.5, borderRadius: RADIUS.md, whiteSpace: 'normal',
                textTransform: 'none', letterSpacing: 0, fontWeight: TYPO.weights.normal }}>{napoveda}</span>}
            </span>
          )}
          <div style={{ flex: 1 }} />
          {akcie}
        </div>
      )}
      {children}
    </section>
  )
}

// TELO KARTY — sivá plocha, na ktorú sa kladú sekcie-karty (61.10). Rolovanie je tu, nie v sekciách.
export function TeloKarty({ children, style = {} }) {
  return (
    <div style={{ flex: 1, minHeight: 0, overflow: 'auto', background: COLORS.page, padding: SPACE.md, ...style }}>{children}</div>
  )
}

// DETAIL ROW — riadok „popis · hodnota" (0.J: dovtedy 9 kópií v obrazovkách). sirka = šírka popisu (Vozidlá 170).
export function DetailRow({ label, value, sirka = 140 }) {
  return (
    <div style={{ display: 'flex', gap: SPACE.md, padding: '4px 0' }}>
      <span style={{ width: sirka, color: COLORS.muted, flexShrink: 0 }}>{label}</span>
      <span style={{ color: COLORS.text, minWidth: 0, wordBreak: 'break-word' }}>{value || '—'}</span>
    </div>
  )
}
